import React, { useEffect, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../sp-firebase/firebase';
import { AuthFlowProvider, useAuthFlow } from '../../contexts/AuthFlowContext';
import { AppSessionProvider, useAppSession } from '../../contexts/AppSessionContext';
import { SpotLockUser } from '../../sp-types/userType';

// Align env access with other services and avoid TS complaints about import.meta
const API_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:8080';

// Track which Firebase UID has already been verified in this tab during
// the current JS runtime session. This is independent of React renders
// and ensures we never call /verify-token more than once per uid per tab.
let verifiedUidThisSession: string | null = null;

interface AuthBootstrapProps {
  children: React.ReactNode;
}

function AuthBootstrapInner({ children }: AuthBootstrapProps) {
  const [ready, setReady] = useState(false);
  const { setMessage, setIsRedirecting } = useAuthFlow();
  const { setSession, clearSession, setFirebaseReady } = useAppSession();

  // Hydrate session from sessionStorage on mount (only once)
  useEffect(() => {
    const existingUserStr = sessionStorage.getItem('user');
    const existingUid = sessionStorage.getItem('uid');

    if (existingUserStr && existingUid) {
      try {
        const userData = JSON.parse(existingUserStr);
        const spotlockUser: SpotLockUser = {
          id: userData.id,
          email: userData.email,
          name: userData.name || userData.email,
          accountId: userData.accountId || '',
          accountName: userData.accountName,
          role: userData.role || 'viewer' as any,
          status: 'active',
          country: 'IL',
          isSuperadmin: userData.isSuperadmin || false,
          isOwner: userData.isOwner || false,
          isAdmin: userData.isSuperadmin || userData.isAdmin || false,
          authProvider: 'google',
          isEditEnabled: userData.isEditEnabled !== undefined ? userData.isEditEnabled : true,
          isApproved: userData.isApproved !== undefined ? userData.isApproved : true,
        };
        setSession('authenticated', spotlockUser);

        // Mark this uid as already verified for this tab so we don't
        // re-call /verify-token for the same Firebase user on reloads.
        verifiedUidThisSession = existingUid;
      } catch (e) {
        console.error('[AuthBootstrap] ❌ Failed to hydrate session:', e);
        clearSession();
      }
    } else {
      // No persisted session in this tab yet
      clearSession();
      verifiedUidThisSession = null;
    }
  }, []); // Run only once on mount

  useEffect(() => {
    let unsubscribe = () => {};
    let isProcessing = false;

    (async () => {
      // Set up auth state observer - central place to verify tokens and manage session
      unsubscribe = onAuthStateChanged(auth, async (user) => {
        const existingUser = sessionStorage.getItem('user');
        const existingUid = sessionStorage.getItem('uid');

        const currentUid = user?.uid || null;
        const hasVerifiedThisSession = !!currentUid && currentUid === verifiedUidThisSession;

        // Determine if we need to verify this Firebase user with the backend.
        // Guarded purely by the per-tab verifiedUidThisSession flag so even if
        // React re-mounts components or effects, we only verify once per uid.
        const needsVerification = !!user && !hasVerifiedThisSession;

        if (user && needsVerification && !isProcessing) {
          isProcessing = true;

          // Show success/info message when returning from login page
          if (window.location.pathname === '/login') {
            setMessage({
              type: 'info',
              text: `Welcome, ${user.displayName || user.email}! Verifying your access...`,
            });
            setIsRedirecting(true);
          }

          try {
            const idToken = await user.getIdToken();
            const response = await fetch(`${API_URL}/api/v2/auth/verify-token`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ idToken }),
            });

            const data = await response.json();

            if (!response.ok || !data.success) {
              console.error('[AuthBootstrap] Token verification failed:', data.error);
              setMessage({
                type: 'error',
                text: data.error || 'Login failed',
              });
              setIsRedirecting(false);
              // Sign out on error to avoid inconsistent state
              await auth.signOut();
              clearSession();
              sessionStorage.removeItem('uid');
              sessionStorage.removeItem('user');
            } else {
              console.log('[AuthBootstrap] Token verified, user authenticated');

              console.log('[AuthBootstrap] API response data.user:', data.user);
              
              const spotlockUser: SpotLockUser = {
                id: data.user.id,
                email: data.user.email,
                name: data.user.name || data.user.email,
                accountId: data.user.accountId || '',
                accountName: data.user.accountName,
                role: data.user.role || 'viewer' as any,
                status: 'active',
                country: 'IL',
                isSuperadmin: data.user.isSuperadmin || false,
                isOwner: data.user.isOwner || false,
                // Keep isAdmin as a backward-compatible alias
                isAdmin: data.user.isSuperadmin || data.user.isAdmin || false,
                authProvider: 'google',
                isEditEnabled: data.user.isEditEnabled !== undefined ? data.user.isEditEnabled : true,
                isApproved: data.user.isApproved !== undefined ? data.user.isApproved : true,
              };
              
              console.log('[AuthBootstrap] Created spotlockUser:', spotlockUser);

              // Persist for this tab and remember which uid we've verified.
              if (user) {
                verifiedUidThisSession = user.uid;
                sessionStorage.setItem('uid', user.uid);
              }
              sessionStorage.setItem('user', JSON.stringify(data.user));

              // Update app session state (source of truth for components)
              setSession('authenticated', spotlockUser);

              setMessage({
                type: 'success',
                text: `Welcome back, ${data.user.name || data.user.email}!`,
              });

              if (window.location.pathname === '/login') {
                console.log('[AuthBootstrap] Redirecting to dashboard in 1s...');
                setTimeout(() => {
                  window.location.href = '/';
                }, 1000);
              }
            }
          } catch (error: any) {
            console.error('[AuthBootstrap] Token verification error:', error);
            setMessage({
              type: 'error',
              text: error.message || 'Authentication failed',
            });
            setIsRedirecting(false);
            clearSession();
            await auth.signOut();
            sessionStorage.removeItem('uid');
            sessionStorage.removeItem('user');
          } finally {
            isProcessing = false;
          }
        } else if (!user) {
          // Firebase reports signed out; clear any local session
          clearSession();
          sessionStorage.removeItem('uid');
          sessionStorage.removeItem('user');
          verifiedUidThisSession = null;
          setIsRedirecting(false);

          console.log('[AuthBootstrap] 🔐 Session cleared after sign-out', {
            hadStoredUser: !!existingUser,
            hadStoredUid: !!existingUid,
          });
        }

        setFirebaseReady(true);
        setReady(true);
      });
    })();

    return () => unsubscribe();
  }, []);

  return <>{children}</>;
}

export function AuthBootstrap({ children }: AuthBootstrapProps) {
  return (
    <AppSessionProvider>
      <AuthFlowProvider>
        <AuthBootstrapInner>{children}</AuthBootstrapInner>
      </AuthFlowProvider>
    </AppSessionProvider>
  );
}
